import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiClock, FiMapPin, FiStar, FiSearch, FiArrowLeft, FiShare2, FiUser } from 'react-icons/fi';
import api from '@/services/api';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ProductCard from '@/components/cards/ProductCard';

const PublicStorePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, getTotalItems } = useCart();
  const { user } = useAuth();
  
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadStore();
  }, [slug]);

  const loadStore = async () => {
    try {
      const response = await api.get(`/public/store/${slug}/products`);
      setStore(response.data.store);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(store.id, product);
  };

  const handleProductClick = (product) => {
    // Navigate to product detail page
    navigate(`/produto/${product.id}`);
  };

  const handleCheckout = () => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/loja/${slug}`);
    } else {
      navigate(`/cliente/carrinho`);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: store?.name,
          text: `Confira os produtos da ${store?.name}!`,
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Loja não encontrada</h1>
        <p className="text-gray-500 mb-4">O link pode estar incorreto ou a loja não existe mais.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-[#DC143C] text-white rounded-lg hover:bg-[#B01030]"
        >
          Ir para página inicial
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#333] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <FiArrowLeft className="text-xl" />
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="p-2 hover:bg-white/10 rounded-full transition-colors relative"
              >
                <FiShare2 className="text-xl" />
                {copied && (
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs bg-green-500 text-white px-2 py-1 rounded whitespace-nowrap">
                    Link copiado!
                  </span>
                )}
              </button>
              
              <button
                onClick={handleCheckout}
                className="relative p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <FiShoppingCart className="text-xl" />
                {getTotalItems() > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC143C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {getTotalItems()}
                  </span>
                )}
              </button>

              {user ? (
                <button
                  onClick={() => navigate(`/${user.user_type}`)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <FiUser className="text-xl" />
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/login?redirect=/loja/${slug}`)}
                  className="px-3 py-1 text-sm bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                >
                  Entrar
                </button>
              )}
            </div>
          </div>

          {/* Store Info */}
          <div className="flex items-center gap-4">
            {store.logo ? (
              <img 
                src={store.logo} 
                alt={store.name}
                className="w-16 h-16 rounded-xl object-cover bg-white"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center text-2xl font-bold">
                {store.name.charAt(0)}
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="text-xl font-bold">{store.name}</h1>
              <div className="flex items-center gap-3 mt-1 text-sm text-white/70">
                <span className={`flex items-center gap-1 ${store.is_open ? 'text-green-400' : 'text-orange-400'}`}>
                  <FiClock className="text-xs" />
                  {store.is_open ? 'Aberta' : 'Fechada'}
                </span>
                {store.rating > 0 && (
                  <span className="flex items-center gap-1">
                    <FiStar className="text-yellow-400" />
                    {store.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-4">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Buscar em ${store.name}...`}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#DC143C]"
            />
          </div>
        </div>
      </header>

      {/* Store Closed Banner */}
      {!store.is_open && (
        <div className="bg-orange-50 border-b border-orange-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-orange-700">
            <FiClock />
            <span className="text-sm">
              <strong>Loja fechada</strong> - Você pode comprar agora e o pedido será preparado quando a loja abrir.
            </span>
          </div>
        </div>
      )}

      {/* Products */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchQuery ? 'Nenhum produto encontrado' : 'Esta loja ainda não tem produtos'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={() => handleAddToCart(product)}
                onClick={() => handleProductClick(product)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Floating Cart Button */}
      {getTotalItems() > 0 && (
        <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto">
          <button
            onClick={handleCheckout}
            className="w-full py-4 bg-[#DC143C] text-white rounded-2xl font-semibold shadow-lg flex items-center justify-center gap-3 hover:bg-[#B01030] transition-colors"
          >
            <FiShoppingCart className="text-xl" />
            <span>Ver Carrinho ({getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'})</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicStorePage;
